import { LogIn, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [forgotPassword, setForgotPassword] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  const { login, loginWithGoogle, resetPassword } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (forgotPassword) {
        await resetPassword(email)
        setResetSent(true)
      } else {
        await login(email, password)
        navigate('/')
      }
    } catch (err: unknown) {
      // Friendly error message for login failures
      setError('Login ou senha incorretos. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setError('')
    setGoogleLoading(true)
    try {
      await loginWithGoogle()
      navigate('/')
    } catch (err: unknown) {
      setError('Erro ao entrar com Google. Tente novamente.')
    } finally {
      setGoogleLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center p-4 flex-grow">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-noe-brand animate-pulse" />
            <span className="text-xl font-bold tracking-wide">NOÉ CONNECT</span>
          </div>
          <h1 className="text-4xl font-bold font-serif tracking-tight mt-4">
            {forgotPassword ? 'Redefinir sua senha' : 'Bem-vindo ao Noé Connect'}
          </h1>
          <p className="text-noe-400 mt-2">
            {forgotPassword 
              ? 'Digite seu e-mail para receber um link de redefinição' 
              : 'Acesse sua conta para gerenciar anúncios e serviços'}
          </p>
        </div>

        {/* Login card */}
        <div className="bg-noe-900/30 border border-noe-800 rounded-[2rem] p-8 backdrop-blur-xl">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3 text-red-300">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {resetSent ? (
             <div className="mb-6 p-4 bg-noe-brand/10 border border-noe-800 rounded-xl text-center">
              <p className="text-noe-brand font-medium">E-mail de redefinição enviado!</p>
              <p className="text-noe-400 text-sm mt-1">Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.</p>
              <button
                onClick={() => {
                  setForgotPassword(false)
                  setResetSent(false)
                }}
                className="mt-4 text-noe-brand hover:text-noe-brandHover text-sm font-medium"
              >
                Voltar para o login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-noe-300 mb-2">
                  E-mail
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                    placeholder="seu@email.com"
                    required
                  />
                </div>
              </div>

              {!forgotPassword && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-noe-300">
                        Senha
                      </label>
                      <button
                        type="button"
                        onClick={() => setForgotPassword(true)}
                        className="text-sm text-noe-brand hover:text-noe-brandHover transition-colors"
                      >
                        Esqueceu a senha?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-noe-500 hover:text-noe-300 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="remember"
                      className="w-4 h-4 rounded border-noe-700 bg-noe-900 text-noe-brand focus:ring-noe-brand focus:ring-offset-noe-950"
                    />
                    <label htmlFor="remember" className="text-sm text-noe-300">
                      Lembrar-me por 30 dias
                    </label>
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-6 bg-noe-brand text-white font-medium rounded-xl hover:bg-noe-brandHover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    {forgotPassword ? 'Enviando...' : 'Entrando...'}
                  </>
                ) : (
                  <>
                    {forgotPassword ? (
                      <>
                        <Mail className="w-5 h-5" />
                        Enviar link de redefinição
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5" />
                        Entrar na plataforma
                      </>
                    )}
                  </>
                )}
              </button>

              {!forgotPassword && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-noe-800"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-noe-900/30 text-noe-500">ou continue com</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={googleLoading}
                    className="w-full py-3 px-6 bg-white text-gray-800 font-medium rounded-xl hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3"
                  >
                    {googleLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-800"></div>
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Entrar com Google
                      </>
                    )}
                  </button>
                </>
              )}

              {forgotPassword && (
                <button
                  type="button"
                  onClick={() => setForgotPassword(false)}
                  className="w-full text-center text-noe-brand hover:text-noe-brandHover text-sm font-medium"
                >
                  Voltar para o login
                </button>
              )}
            </form>
          )}

          <div className="mt-8 pt-8 border-t border-noe-800">
             <p className="text-center text-noe-500 text-sm">
               Não tem uma conta?{' '}
                <Link to="/register" className="text-noe-brand hover:text-noe-brandHover font-medium">
                  Solicitar acesso
                </Link>
             </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="mt-10 text-center">
           <div className="inline-flex items-center gap-6 text-noe-600 text-sm">
             <span>Login seguro</span>
             <span>•</span>
             <span>Conexão criptografada</span>
             <span>•</span>
             <span>Conformidade GDPR</span>
           </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage